import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Search, Users, FileSearch, FileText, TrendingUp, Clock3, ShoppingCart, Truck, MonitorSmartphone, Target, ShieldCheck, PiggyBank, Snowflake, Flame, Ambulance, Droplets, Waves, Siren, HandHelping, Tractor, Construction } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { PageHeader, SectionHeader, RichSection } from '../components/Shared';
import { FactCard, PositionBox, FaqList } from '../components/FactCheck';
import { NextStepBand } from '../components/NextStepBand';

const ACCESSED = 'August 25, 2026';

const REVIEW_AREAS = [
  { icon: Search, title: 'Find waste and duplication', body: 'Line by line: where is the same work paid for twice, or paid for and not delivered?' },
  { icon: Users, title: 'Review management costs', body: 'How many layers of management sit between residents and the front line, and what do they cost?' },
  { icon: FileSearch, title: 'Review consultants', body: 'What is being outsourced to consultants that staff could do, and what did we get for it?' },
  { icon: FileText, title: 'Review contracts and vendors', body: 'Are contracts competitive, renewed on autopilot, or priced above market?' },
  { icon: TrendingUp, title: 'Review staffing growth', body: 'Is the workforce growing faster than the population and the services residents receive?' },
  { icon: Clock3, title: 'Examine overtime', body: 'Chronic overtime is a red flag for staffing, scheduling, or management problems.' },
  { icon: ShoppingCart, title: 'Examine procurement', body: 'How the City buys is as important as what it buys. Open, competitive, documented.' },
  { icon: Truck, title: 'Examine fleet costs', body: 'Vehicles, fuel, maintenance, and replacement schedules: are we getting full value?' },
  { icon: MonitorSmartphone, title: 'Examine technology and software contracts', body: 'Licence sprawl and overlapping systems quietly drain budgets every single year.' },
  { icon: Target, title: 'Measure results', body: 'Every dollar should map to a result a resident can see. If it cannot be measured, ask why.' },
];

const PROTECTED_SERVICES = [
  { icon: Construction, label: 'Roads' },
  { icon: Snowflake, label: 'Snow removal' },
  { icon: Flame, label: 'Fire' },
  { icon: Ambulance, label: 'Paramedics' },
  { icon: Droplets, label: 'Water' },
  { icon: Waves, label: 'Wastewater' },
  { icon: Siren, label: 'Emergency services' },
  { icon: HandHelping, label: 'Front-line services' },
  { icon: Tractor, label: 'Rural services' },
];

const FAQS = [
  { q: 'Does cutting costs mean cutting services?', a: 'No. Steve\'s position is the opposite: cut waste BEFORE cutting services. The point of finding waste, duplication, and poor contracts is to protect the services residents actually rely on.' },
  { q: 'Where would the review start?', a: 'With the biggest and least visible costs: management structure, consultants, contracts, technology spending, fleet, and procurement. The Savings Tracker shows what Steve is examining, item by item, with sources.' },
  { q: 'What happens to any savings that are found?', a: 'Steve\'s position is that genuine, verified savings should be returned to taxpayers where possible, or redirected to protected front-line services. The Savings Tracker separates verified savings from estimates so nothing is overpromised.' },
  { q: 'Is this realistic for one councillor to do?', a: 'One councillor cannot do it alone, and Steve does not claim otherwise. What one councillor can do is ask documented questions in public, propose reviews, publish what is found, and build the tools that let residents watch. That is what this platform is built to do.' },
];

export default function LowerTaxes() {
  return (
    <div>
      <PageHeader
        pageKey="lower-taxes"
        kicker="A MAJOR CAMPAIGN SECTION"
        title="CUT THE COST OF GOVERNMENT BEFORE ASKING TAXPAYERS FOR MORE."
        subtitle="Lower taxes through responsible spending: find the waste first, protect the services people rely on, and show the work in public."
      />
      <RichSection id="lower-taxes-sections.intro" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8" />

      {/* Where taxes stand: verified facts */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14" data-testid="lower-taxes-facts-section">
        <SectionHeader kicker="THE STARTING POINT" title="Where City taxes stand today" dek="Facts first, from official City records." />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FactCard kind="official_source" title="The 2026 City budget totals $472 million"
            cite={{ source: 'City of Kawartha Lakes, 2026 Budget adoption notice', year: '2025', accessed: ACCESSED, url: 'https://www.kawarthalakes.ca/news-and-notices/posts/kawartha-lakes-council-adopts-the-2026-budget-centered-on-strategic-priorities/' }}>
            Council adopted the 2026 budget on November 25, 2025: $472 million across operating, capital, special projects, and water and wastewater.
          </FactCard>
          <FactCard kind="official_source" title="The 2026 tax levy increase is 5.9%"
            cite={{ source: 'City of Kawartha Lakes, 2026 Budget adoption notice', year: '2025', accessed: ACCESSED, url: 'https://www.kawarthalakes.ca/news-and-notices/posts/kawartha-lakes-council-adopts-the-2026-budget-centered-on-strategic-priorities/' }}>
            2.9% for general operations, 2.0% for the dedicated capital levy, and 1.0% for special area rates (policing and Lindsay Transit). For an average home assessed at $267,000, the general and capital portions average about $160 more per year.
          </FactCard>
        </div>
        <div className="mt-4">
          <PositionBox title="CUT WASTE BEFORE CUTTING SERVICES." testId="lower-taxes-position">
            <p>Before asking taxpayers for more, government should prove it is spending what it already collects responsibly. Find the waste, fix the structure, measure the results, and return genuine savings to taxpayers where possible.</p>
          </PositionBox>
        </div>
      </section>

      {/* The review plan */}
      <section className="bg-white border-y border-slate-200 py-10 md:py-14" data-testid="lower-taxes-review-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader kicker="THE PLAN" title="Ten places to look, in the open" dek="Each review is tracked publicly in the Savings Tracker, with sources, so residents can follow the work." />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {REVIEW_AREAS.map(({ icon: Icon, title, body }) => (
              <Card key={title} className="card-lift" data-testid="lower-taxes-review-card">
                <CardContent className="p-5">
                  <div className="h-10 w-10 rounded-xl bg-[#1a2744] flex items-center justify-center mb-3">
                    <Icon className="h-5 w-5 text-[#C9A84C]" />
                  </div>
                  <p className="font-serif font-bold text-[#1a2744] leading-snug">{title}</p>
                  <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{body}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Protected services */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14" data-testid="lower-taxes-protected-section">
        <SectionHeader kicker="THE LINE THAT DOES NOT MOVE" title="Essential services are protected" dek="Responsible spending means protecting what residents rely on every day." />
        <div className="flex flex-wrap gap-2.5" data-testid="protected-services-list">
          {PROTECTED_SERVICES.map(({ icon: Icon, label }) => (
            <span key={label} className="inline-flex items-center gap-2 min-h-[44px] px-4 py-2 rounded-full bg-white border border-slate-200 text-sm font-bold text-[#1a2744]" data-testid="protected-service">
              <ShieldCheck className="h-4 w-4 text-emerald-600" /> <Icon className="h-4 w-4 text-[#8a6f22]" /> {label}
            </span>
          ))}
        </div>

        {/* Connected tools */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-10">
          <Card className="card-lift">
            <CardContent className="p-5 flex flex-col h-full">
              <PiggyBank className="h-6 w-6 text-[#C9A84C] mb-2" />
              <p className="font-serif font-bold text-[#1a2744]">Savings Tracker</p>
              <p className="text-sm text-slate-600 mt-1.5 flex-1">Every spending question Steve is tracking, with status and sources.</p>
              <Button asChild className="mt-3 h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press w-full" data-testid="lower-taxes-savings-button"><Link to="/savings-tracker">Open the Tracker <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
            </CardContent>
          </Card>
          <Card className="card-lift">
            <CardContent className="p-5 flex flex-col h-full">
              <FileSearch className="h-6 w-6 text-[#C9A84C] mb-2" />
              <p className="font-serif font-bold text-[#1a2744]">Independent Forensic Audit</p>
              <p className="text-sm text-slate-600 mt-1.5 flex-1">Why Steve supports independent professional scrutiny of City spending.</p>
              <Button asChild className="mt-3 h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press w-full" data-testid="lower-taxes-audit-button"><Link to="/forensic-audit">Why an Audit <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
            </CardContent>
          </Card>
          <Card className="card-lift">
            <CardContent className="p-5 flex flex-col h-full">
              <FileText className="h-6 w-6 text-[#C9A84C] mb-2" />
              <p className="font-serif font-bold text-[#1a2744]">City Finances, Explained</p>
              <p className="text-sm text-slate-600 mt-1.5 flex-1">The budget, the levy, reserves, and debt, in plain language with sources.</p>
              <Button asChild className="mt-3 h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press w-full" data-testid="lower-taxes-finances-button"><Link to="/city-finances">See the Numbers <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white border-y border-slate-200 py-10 md:py-14" data-testid="lower-taxes-faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader kicker="PLAIN ANSWERS" title="Common questions" />
          <FaqList items={FAQS} testId="lower-taxes-faq" />
        </div>
      </section>

      {/* One clear next step */}
      <div className="pt-10">
        <NextStepBand
          title="See where the money actually goes."
          buttonLabel="Follow the Money"
          to="/city-finances"
          secondaryLabel="Or open the Savings Tracker"
          secondaryTo="/savings-tracker"
          testId="lower-taxes-next-step"
        />
      </div>
    </div>
  );
}
